import { useCallback, useEffect, useRef, useState } from 'react'

import { useApolloClient } from '@apollo/client'
import { datadogRum } from '@datadog/browser-rum'

import { useMutation } from '@redwoodjs/web'

import {
  CREATE_DOCUMENT,
  DELETE_DOCUMENT,
  GET_DOCUMENT_INTELLIGENCE,
  UPDATE_DOCUMENT,
} from 'src/graphql/documents.graphql'
import {
  DocumentCategory,
  DocumentUserStatus,
  UploadStatus,
} from 'src/graphql/types/documents'
import { fileContentTypeFromMimeType } from 'src/lib/documentFileType'
import { normalizeLicenseNumber } from 'src/lib/documentIntelligence'

// Confidence gating happens server-side: the documentIntelligence resolver
// drops predictions below AI_MIN_DISPLAY_CONFIDENCE_THRESHOLD (0.75) before
// they reach this hook, so any prediction we receive is confident enough to
// prefill. A field the model wasn't sure about simply has no prediction here.

// Poll the document-intelligence query this often while AI runs server-side.
const POLL_INTERVAL_MS = 2000

// Give up if AI never resolves. Kept generous because the driver is on mobile
// data and the S3 upload + classification can be slow. A timeout surfaces the
// same "that didn't look like a CDL" message as an outright rejection.
const TIMEOUT_MS = 45000

export type CdlAutofillStatus =
  | 'idle'
  | 'uploading'
  | 'analyzing'
  | 'filled'
  // Not a CDL: classification came back as another category (or not
  // confidently CDL), or the backend archived the upload. Inline banner.
  | 'rejected'
  // It IS a CDL, but the read was too poor to be useful: 3+ of the AI-fillable
  // fields were filtered out. Full-screen retake-first takeover.
  | 'unreadable'
  | 'error'

// Which entry point supplied the file — the RUM `route` dimension (ENG-4304).
// 'take_photo' is the camera capture input; 'photo_library' and 'browse_files'
// share the mobile picker. The retake takeover's buttons reuse the same routes
// (camera → take_photo, upload → photo_library). 'desktop_upload' is the
// desktop rail (ENG-4358) — drag-drop and click-to-browse both report it.
export type CdlScanSource =
  | 'take_photo'
  | 'photo_library'
  | 'browse_files'
  | 'desktop_upload'

// The license fields the scan can prefill: number, state, class, expiration,
// endorsements. Used to decide 'filled' vs 'unreadable'.
const AI_FILLABLE_FIELD_COUNT = 5

// A confident CDL still routes to the retake takeover when 3 or more of the
// AI-fillable fields were filtered out — a read that sparse means
// a bad photo, and retaking beats hand-fixing most of the form.
const UNREADABLE_MISSING_THRESHOLD = 3

// Prefilled license fields the AI was confident about. Any field the model
// abstained on (or was filtered by the backend) is omitted so the driver fills it
// in manually. `expirationDate` is a local-midnight Date matching the form's
// license.expirationDate shape. `endorsements` is the validated code list
// (e.g. ['H', 'N']) — the extractor already dropped codes outside the
// Endorsement enum.
export interface CdlPrefill {
  licenseNumber?: string
  state?: string
  licenseClass?: string
  expirationDate?: Date
  endorsements?: string[]
}

// Per-field confidence for the prefilled values (0–1), keyed like CdlPrefill.
// Lets the form flag borderline reads (returned by the backend but below
// HIGH_CONFIDENCE) with a "Double-check this" nudge.
export type CdlPrefillConfidence = Partial<Record<keyof CdlPrefill, number>>

// At/above this, a prefilled value renders without a nudge; below it (but above
// the backend's display threshold, or it wouldn't prefill at all) the field gets "Double-check
// this". Mirrors the backend's AI_HIGH_CONFIDENCE_THRESHOLD auto-apply bar.
export const HIGH_CONFIDENCE_THRESHOLD = 0.9

interface DocumentPrediction {
  field: string
  confidence?: number | null
  textValue?: string | null
  categoryValue?: string | null
  expirationValue?: string | null
}

// Rebuilds a stored UTC-midnight instant as a local-midnight Date on the same
// calendar day, so the license form (which formats with dayjs.utc) shows the
// correct day regardless of the browser timezone. Mirrors UploadDocumentModal.
const toLocalDate = (isoValue: string): Date => {
  const utc = new Date(isoValue)
  return new Date(utc.getUTCFullYear(), utc.getUTCMonth(), utc.getUTCDate())
}

export interface UseDriverCdlAutofillResult {
  status: CdlAutofillStatus
  prefill: CdlPrefill | null
  /** Confidence per prefilled field; null until a scan resolves to 'filled'. */
  confidence: CdlPrefillConfidence | null
  /**
   * interaction is desktop-only (ENG-4362): 'drag' vs 'browse' on the rail.
   * The route stays a single 'desktop_upload' so funnels don't fragment.
   */
  start: (file: File, source: CdlScanSource, interaction?: 'drag' | 'browse') => void
  reset: () => void
}

/**
 * Orchestrates the driver-facing CDL autofill:
 * createDocument → PUT file to S3 → updateDocument(COMPLETED) → poll
 * documentIntelligence → resolve the extracted license fields.
 *
 * The category is always OTHER on create — the server-side AI classifies it and
 * archives the document (making the intelligence query return null) when the
 * upload is not a CDL, which we surface as 'rejected'.
 */
export const useDriverCdlAutofill = (
  applicantId: string | null | undefined,
  // Whether the application already carried saved license data when the step
  // loaded — the RUM returning_applicant dimension (ENG-4362), same signal as
  // the "Double-check this" conflict chips.
  returningApplicant = false
): UseDriverCdlAutofillResult => {
  const client = useApolloClient()
  const [status, setStatus] = useState<CdlAutofillStatus>('idle')
  const [prefill, setPrefill] = useState<CdlPrefill | null>(null)
  const [confidence, setConfidence] = useState<CdlPrefillConfidence | null>(
    null
  )

  const [createDocument] = useMutation(CREATE_DOCUMENT)
  const [updateDocument] = useMutation(UPDATE_DOCUMENT)
  const [deleteDocument] = useMutation(DELETE_DOCUMENT)

  // Poll bookkeeping. Refs (not state) so the interval callback always sees the
  // live values without re-subscribing, and so cleanup can clear them.
  const pollTimer = useRef<ReturnType<typeof setInterval> | null>(null)
  const timeoutTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Guards against a late poll or timeout mutating state after the caller reset
  // (e.g. tapped Rescan) or the component unmounted. Bumping it invalidates any
  // in-flight run.
  const runId = useRef(0)

  const clearTimers = useCallback(() => {
    if (pollTimer.current) {
      clearInterval(pollTimer.current)
      pollTimer.current = null
    }
    if (timeoutTimer.current) {
      clearTimeout(timeoutTimer.current)
      timeoutTimer.current = null
    }
  }, [])

  // Invalidates any in-flight run and clears its timers. Returns the new run id
  // so callers that continue can tag their work with it. Shared by reset() and
  // start() so the invalidation protocol lives in one place.
  const invalidate = useCallback((): number => {
    runId.current += 1
    clearTimers()
    return runId.current
  }, [clearTimers])

  // RUM bookkeeping for the in-flight scan (ENG-4304): which entry route
  // supplied the file and when it started, so the terminal action carries the
  // route dimension and the end-to-end AI-fill duration. Null when no scan is
  // in flight (set by start, cleared by the terminal emit).
  // Live mirror for the stable RUM callbacks below.
  const returningApplicantRef = useRef(returningApplicant)
  returningApplicantRef.current = returningApplicant

  const scanMetaRef = useRef<{
    source: CdlScanSource
    startedAt: number
  } | null>(null)

  // Emits the single terminal RUM action for the in-flight scan. No-ops when
  // nothing is in flight, so late/duplicate callers (e.g. a Rescan after the
  // 'filled' action already fired) don't double-count.
  const trackScanEnd = useCallback(
    (outcome: string, extra: Record<string, unknown> = {}) => {
      const meta = scanMetaRef.current
      if (!meta) return
      scanMetaRef.current = null
      datadogRum.addAction('driver_cdl_scan_ended', {
        component: 'useDriverCdlAutofill',
        route: meta.source,
        outcome,
        duration_ms: Date.now() - meta.startedAt,
        returning_applicant: returningApplicantRef.current,
        ...extra,
      })
    },
    []
  )

  // The document created by the most recent scan. Tracked so a superseded or
  // abandoned scan (Rescan / Try again / Skip / picking a new file) can be
  // discarded server-side instead of piling up as ACTIVE docs in the applicant
  // folder — one scan flow should net at most one kept document.
  const lastDocumentIdRef = useRef<string | null>(null)

  const discardPreviousScan = useCallback(() => {
    const prevId = lastDocumentIdRef.current
    if (!prevId || !applicantId) return
    lastDocumentIdRef.current = null
    // Fire-and-forget: failures are non-fatal (e.g. the doc was already
    // archived server-side after a rejected classification, or the driver is
    // offline — nothing user-visible depends on this cleanup).
    deleteDocument({ variables: { id: prevId, applicantId } }).catch(() => {})
  }, [applicantId, deleteDocument])

  const reset = useCallback(() => {
    // A reset with a scan still in flight is the driver bailing out (the Skip
    // link while analyzing, or abandoning an upload) — the terminal actions
    // for finished scans already fired, so this only ever emits mid-flight.
    trackScanEnd('abandoned')
    invalidate()
    discardPreviousScan()
    setStatus('idle')
    setPrefill(null)
    setConfidence(null)
  }, [invalidate, discardPreviousScan, trackScanEnd])

  // Live mirror of `status` for the unmount cleanup below — a state read
  // inside the cleanup closure would be stale.
  const statusRef = useRef<CdlAutofillStatus>('idle')
  statusRef.current = status

  // Unmount cleanup: stop the poll, and discard a scan the driver walked away
  // from (header back during uploading/analyzing, or leaving the unreadable
  // takeover). Those documents are ACTIVE (or about to be) with nobody left to
  // resolve them — without this they'd linger in the applicant folder. A
  // 'filled' scan is a kept CDL and must NOT be discarded: Continue unmounts
  // the screen too. 'rejected'/'error' documents were already archived or
  // discarded server-side.
  useEffect(() => {
    return () => {
      clearTimers()
      // A scan still in flight at unmount was abandoned by navigation — emit
      // its terminal action so every scan_started has exactly one scan_ended.
      // No-ops when the scan already ended ('unreadable'/'filled'/... cleared
      // the in-flight meta), so this can't double-count.
      trackScanEnd('abandoned')
      if (
        statusRef.current === 'uploading' ||
        statusRef.current === 'analyzing' ||
        statusRef.current === 'unreadable'
      ) {
        discardPreviousScan()
      }
    }
  }, [clearTimers, discardPreviousScan, trackScanEnd])

  const start = useCallback(
    async (
      file: File,
      source: CdlScanSource,
      interaction?: 'drag' | 'browse'
    ) => {
      if (!applicantId) {
        setStatus('error')
        return
      }

      // A scan still in flight when a new one starts was replaced mid-run
      // (picked a new file without resetting first).
      trackScanEnd('superseded')
      scanMetaRef.current = { source, startedAt: Date.now() }
      datadogRum.addAction('driver_cdl_scan_started', {
        component: 'useDriverCdlAutofill',
        route: source,
        file_type: file.type,
        file_size: file.size,
        returning_applicant: returningApplicantRef.current,
        ...(interaction && { interaction }),
      })

      // New attempt: invalidate any in-flight run, discard the previous scan's
      // document (a rescan supersedes it), and reset bookkeeping.
      const thisRun = invalidate()
      discardPreviousScan()
      setPrefill(null)
      setConfidence(null)
      setStatus('uploading')

      const fileType = fileContentTypeFromMimeType(file.type)

      try {
        // 1. Create the document record; category OTHER lets AI classify.
        // userStatus is required (non-null) by CreateDocumentInput; PENDING_REVIEW
        // matches the recruiter upload default.
        const { data: createData } = await createDocument({
          variables: {
            input: {
              applicantId,
              category: DocumentCategory.OTHER,
              userStatus: DocumentUserStatus.PENDING_REVIEW,
              fileType,
              fileName: file.name,
            },
          },
        })
        if (thisRun !== runId.current) return

        const uploadUrl: string | undefined =
          createData?.createDocument?.uploadUrl
        const documentId: string | undefined =
          createData?.createDocument?.documentId
        if (!uploadUrl || !documentId) {
          trackScanEnd('error', { stage: 'create_document' })
          setStatus('error')
          return
        }
        lastDocumentIdRef.current = documentId

        // 2. PUT the raw bytes to the S3 presigned URL (same as recruiter flow).
        const uploadResponse = await fetch(uploadUrl, {
          method: 'PUT',
          body: file,
          headers: { 'Content-Type': file.type },
        })
        if (thisRun !== runId.current) return
        if (!uploadResponse.ok) {
          trackScanEnd('error', { stage: 's3_upload' })
          setStatus('error')
          return
        }

        // 3. Mark the upload complete — this kicks off AI classification.
        await updateDocument({
          variables: {
            id: documentId,
            input: { uploadStatus: UploadStatus.COMPLETED },
          },
        })
        if (thisRun !== runId.current) return

        // 4. Poll document intelligence until COMPLETED / rejected / timeout.
        setStatus('analyzing')

        // Settles this run terminally: bumping runId invalidates any response
        // still in flight (an overlapping poll tick, or the timeout racing a
        // slow poll), so it can't overwrite the status set here — the runId
        // guard after each await drops it.
        const settleRun = () => {
          if (thisRun === runId.current) runId.current += 1
          clearTimers()
        }

        // Deadline handler. Classification can finish in the gap between the
        // last 2s tick and this timer (or a response carrying the good result
        // can be mid-flight when it fires) — so before declaring failure and
        // discarding a scan that may have SUCCEEDED, wait out any in-flight
        // response and make one last-chance poll. Only if the run is still
        // unresolved after that do we settle as a timeout. (pollOnce and
        // pollInFlight are declared below; they're initialized long before
        // this callback can fire.)
        timeoutTimer.current = setTimeout(async () => {
          if (thisRun !== runId.current) return
          clearTimers()
          // Bounded wait so a hung request can't stall the timeout forever.
          for (let waited = 0; pollInFlight && waited < 5000; waited += 250) {
            await new Promise((resolve) => setTimeout(resolve, 250))
          }
          // An in-flight response that reached a terminal state settled the
          // run itself — nothing more to do.
          if (thisRun !== runId.current) return
          await pollOnce()
          if (thisRun !== runId.current) return
          settleRun()
          trackScanEnd('timeout')
          // The document was already activated by the COMPLETED update, and
          // with the poll dead nothing client-side will ever resolve it — if
          // the driver walks away or types manually, an ACTIVE OTHER document
          // would linger in the applicant folder. Discard it server-side
          // (fire-and-forget; classify may still archive it independently).
          discardPreviousScan()
          setStatus('error')
        }, TIMEOUT_MS)

        // Serialized: a tick is skipped while the previous request is still in
        // flight, so a slow response can't interleave with a faster later one.
        let pollInFlight = false
        const pollOnce = async () => {
          if (pollInFlight || thisRun !== runId.current) return
          pollInFlight = true
          try {
            const { data } = await client.query({
              query: GET_DOCUMENT_INTELLIGENCE,
              variables: { id: documentId, applicantId },
              fetchPolicy: 'network-only',
            })
            if (thisRun !== runId.current) return

            const doc = data?.document
            // The record provably exists (we awaited createDocument +
            // updateDocument before polling), so a missing document means it
            // was deleted out from under the poll → treat as rejected.
            if (!doc) {
              settleRun()
              trackScanEnd('rejected', { reason: 'document_missing' })
              setStatus('rejected')
              return
            }

            const intel = doc.documentIntelligence
            // A terminal AI failure resolves immediately rather than polling to
            // the client timeout. Checked BEFORE the archived branch: a failed
            // run's document is also archived server-side, and it must surface
            // as an error, not as the "didn't look like a CDL" copy.
            if (intel?.status === 'FAILED') {
              settleRun()
              trackScanEnd('error', { stage: 'ai_processing' })
              setStatus('error')
              return
            }
            // Archived while polling = the backend hid a driver upload it
            // couldn't confirm as a CDL. Only a run that actually READ the
            // image — COMPLETED with prediction rows (hasAnyPredictions is
            // computed server-side BEFORE the display filter, so a
            // low-confidence classification still counts) — earns the
            // "didn't look like a CDL" copy. Anything else archived means the
            // image was never judged: the pipeline skipped the file
            // (unsupported type / empty / oversized → COMPLETED, zero rows)
            // or never created an intelligence record at all (document
            // intelligence disabled for the company). Those are processing
            // problems → the error copy.
            if (doc.status === 'ARCHIVED') {
              settleRun()
              const classifiedRead =
                intel?.status === 'COMPLETED' &&
                intel?.hasAnyPredictions === true
              if (classifiedRead) {
                trackScanEnd('rejected', { reason: 'archived_non_cdl' })
                setStatus('rejected')
              } else {
                trackScanEnd('error', { stage: 'file_skipped' })
                setStatus('error')
              }
              return
            }
            if (!intel || intel.status !== 'COMPLETED') return

            // Every path below is terminal (rejected or filled).
            settleRun()

            const predictions: DocumentPrediction[] = intel.predictions ?? []
            const category = predictions.find((p) => p.field === 'CATEGORY')
            // Only a confident CDL classification is a success. The backend
            // already drops sub-display-threshold predictions from the query
            // response (filterDisplayablePredictions), so a CATEGORY row that
            // reaches us IS confident — a low-confidence classification shows
            // up here as a missing prediction.
            if (!category || category.categoryValue !== DocumentCategory.CDL) {
              trackScanEnd('rejected', { reason: 'not_confident_cdl' })
              setStatus('rejected')
              return
            }

            const next: CdlPrefill = {}
            const nextConfidence: CdlPrefillConfidence = {}
            for (const p of predictions) {
              switch (p.field) {
                case 'LICENSE_NUMBER':
                  // Empty string means the AI confidently read "no license number"
                  next.licenseNumber = normalizeLicenseNumber(p.textValue ?? '')
                  nextConfidence.licenseNumber = p.confidence ?? 0
                  break
                case 'LICENSE_STATE':
                  // Empty string means the AI confidently read "no state"
                  next.state = p.textValue ?? ''
                  nextConfidence.state = p.confidence ?? 0
                  break
                case 'LICENSE_CLASS':
                  // Empty string means the AI confidently read "no class"
                  next.licenseClass = p.textValue ?? ''
                  nextConfidence.licenseClass = p.confidence ?? 0
                  break
                case 'LICENSE_ENDORSEMENTS':
                  // textValue is the extractor's comma-joined validated codes
                  // ("H,N"). Empty string means the AI confidently read "no
                  // endorsements".
                  next.endorsements = p.textValue ? p.textValue.split(',') : []
                  nextConfidence.endorsements = p.confidence ?? 0
                  break
                case 'DATE':
                  // Unlike the text fields, there's no "confidently empty"
                  // representation for a date — the backend sends an ISO
                  // string or omits the prediction. A truthy guard also keeps
                  // a falsy value from planting an undefined-valued key in
                  // `next`, which would inflate readCount and let a sparse
                  // scan dodge the unreadable/retake threshold.
                  if (p.expirationValue) {
                    next.expirationDate = toLocalDate(p.expirationValue)
                    nextConfidence.expirationDate = p.confidence ?? 0
                  }
                  break
                default:
                  break
              }
            }

            // It's confidently a CDL — now grade the read. Missing =
            // AI-fillable fields that were missing. 3+ missing
            // means the photo was too poor to be worth keeping: route to the
            // retake takeover instead of prefilling a mostly-empty form.
            // 2 or fewer missing → prefill what we got (keep-partial-wins).
            const readCount = Object.keys(next).length
            if (
              AI_FILLABLE_FIELD_COUNT - readCount >=
              UNREADABLE_MISSING_THRESHOLD
            ) {
              trackScanEnd('unreadable', { fields_read: readCount })
              setStatus('unreadable')
              return
            }

            trackScanEnd('filled', { fields_filled: readCount })
            setPrefill(next)
            setConfidence(nextConfidence)
            setStatus('filled')
          } catch {
            if (thisRun !== runId.current) return
            settleRun()
            trackScanEnd('error', { stage: 'poll' })
            setStatus('error')
          } finally {
            pollInFlight = false
          }
        }
        pollTimer.current = setInterval(pollOnce, POLL_INTERVAL_MS)
      } catch {
        if (thisRun !== runId.current) return
        clearTimers()
        trackScanEnd('error', { stage: 'request' })
        setStatus('error')
      }
    },
    [
      applicantId,
      client,
      createDocument,
      updateDocument,
      discardPreviousScan,
      clearTimers,
      invalidate,
      trackScanEnd,
    ]
  )

  return { status, prefill, confidence, start, reset }
}
